import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Star, Phone, DollarSign, Calendar, ArrowLeft, User, Briefcase } from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

const DoctorProfile = () => {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [doctor, setDoctor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBooking, setShowBooking] = useState(false);
  const [appointmentData, setAppointmentData] = useState({
    appointment_date: '',
    reason: ''
  });

  useEffect(() => {
    fetchDoctor();
    fetchReviews();
  }, [doctorId]);

  const fetchDoctor = async () => {
    try {
      const response = await axios.get(`${API}/doctors/${doctorId}`);
      setDoctor(response.data);
    } catch (error) {
      toast.error('Impossible de charger le profil du médecin');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${doctorId}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews', error);
    }
  };

  const handleBookAppointment = async () => {
    if (!user) {
      toast.error('Veuillez vous connecter pour prendre rendez-vous');
      navigate('/login');
      return;
    }

    if (user.role !== 'patient') {
      toast.error('Seuls les patients peuvent prendre rendez-vous');
      return;
    }

    try {
      await axios.post(`${API}/appointments`, {
        doctor_id: doctorId,
        ...appointmentData,
        appointment_date: new Date(appointmentData.appointment_date).toISOString()
      });
      toast.success('Rendez-vous réservé avec succès!');
      setShowBooking(false);
      navigate('/patient-dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de la réservation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!doctor) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="doctor-profile">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Doctor Info Card */}
        <Card className="card mb-8">
          <CardContent className="py-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{doctor.clinic_name}</h1>
                <p className="text-xl text-teal-600 font-medium">{doctor.specialization}</p>
              </div>
              {doctor.rating > 0 && (
                <div className="flex items-center space-x-2 bg-teal-50 px-4 py-2 rounded-full">
                  <Star className="w-5 h-5 fill-current text-teal-600" />
                  <span className="font-bold text-lg">{doctor.rating}</span>
                  <span className="text-gray-600">({doctor.total_reviews})</span>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-teal-600 mt-1" />
                <div>
                  <p className="text-sm text-gray-600">Localisation</p>
                  <p className="font-semibold">{doctor.clinic_address}</p>
                  <p className="text-gray-700">{doctor.city}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-teal-600 mt-1" />
                <div>
                  <p className="text-sm text-gray-600">Téléphone</p>
                  <p className="font-semibold">{doctor.phone}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Briefcase className="w-5 h-5 text-teal-600 mt-1" />
                <div>
                  <p className="text-sm text-gray-600">Expérience</p>
                  <p className="font-semibold">{doctor.experience_years} ans</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <DollarSign className="w-5 h-5 text-teal-600 mt-1" />
                <div>
                  <p className="text-sm text-gray-600">Frais de Consultation</p>
                  <p className="font-semibold text-xl">{doctor.consultation_fee?.toLocaleString()} FCFA</p>
                </div>
              </div>
            </div>

            {doctor.bio && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">À propos</h3>
                <p className="text-gray-700 leading-relaxed">{doctor.bio}</p>
              </div>
            )}

            <Dialog open={showBooking} onOpenChange={setShowBooking}>
              <DialogTrigger asChild>
                <Button className="btn-primary w-full" data-testid="book-appointment-btn">
                  <Calendar className="w-5 h-5 mr-2" />
                  Prendre Rendez-vous
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="booking-dialog">
                <DialogHeader>
                  <DialogTitle>Réserver un Rendez-vous</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date et Heure du Rendez-vous</label>
                    <Input
                      type="datetime-local"
                      value={appointmentData.appointment_date}
                      onChange={(e) => setAppointmentData({ ...appointmentData, appointment_date: e.target.value })}
                      data-testid="appointment-date-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Raison de la Visite</label>
                    <Textarea
                      placeholder="Décrivez vos symptômes ou la raison de la consultation"
                      value={appointmentData.reason}
                      onChange={(e) => setAppointmentData({ ...appointmentData, reason: e.target.value })}
                      rows={4}
                      data-testid="reason-input"
                    />
                  </div>
                  <div className="flex gap-4">
                    <Button onClick={handleBookAppointment} className="btn-primary flex-1" data-testid="confirm-booking-btn">
                      Confirmer la Réservation
                    </Button>
                    <Button onClick={() => setShowBooking(false)} variant="outline" data-testid="cancel-booking-btn">
                      Annuler
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Reviews Section */}
        <Card className="card">
          <CardHeader>
            <CardTitle>Avis des Patients</CardTitle>
          </CardHeader>
          <CardContent>
            {reviews.length === 0 ? (
              <p className="text-center text-gray-600 py-8">Aucun avis pour le moment</p>
            ) : (
              <div className="space-y-4" data-testid="reviews-list">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b pb-4 last:border-b-0" data-testid={`review-${review.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <User className="w-5 h-5 text-gray-400" />
                        <span className="font-medium">{review.patient_name}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${i < review.rating ? 'fill-current text-yellow-400' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                    {review.comment && (
                      <p className="text-gray-700 text-sm">{review.comment}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      {new Date(review.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DoctorProfile;